import { useState, useEffect, useCallback } from 'react';
import { Target, Plus, Clock, CheckCircle, X, AlertCircle, Trophy, RefreshCw, Eye, ChevronDown, ChevronUp, MessageSquare } from '../icons';
import {
  fetchOutcomesForUser, createOutcome, updateOutcome, deleteOutcome,
  addOutcomeMilestone, toggleOutcomeMilestone, deleteOutcomeMilestone,
  fetchGoalsForUser, updateGoal,
  type NlpOutcome, type NlpOutcomeMilestone, type Goal,
} from '../../lib/progress';
import { OutcomeBuilderModal } from './OutcomeBuilderModal';

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-cream-200 text-chocolate-600' },
  proposed: { label: 'Proposed', color: 'bg-amber-100 text-amber-700' },
  accepted: { label: 'Accepted', color: 'bg-sage-100 text-sage-700' },
  active: { label: 'Active', color: 'bg-sage-100 text-sage-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600' },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700' },
  paused: { label: 'Paused', color: 'bg-cream-200 text-chocolate-500' },
};

export function CoachOutcomesSection({ userId, coachId }: { userId: string; coachId: string }) {
  const [outcomes, setOutcomes] = useState<NlpOutcome[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [expandedOutcome, setExpandedOutcome] = useState<string | null>(null);
  const [prefillGoal, setPrefillGoal] = useState<Goal | null>(null);
  const [coachFeedback, setCoachFeedback] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setIsLoading(true);
    const [o, g] = await Promise.all([fetchOutcomesForUser(userId), fetchGoalsForUser(userId)]);
    setOutcomes(o);
    setGoals(g.filter(goal => goal.shared_with_coach));
    setIsLoading(false);
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data: Partial<NlpOutcome>) => {
    await createOutcome(data as any);
    await load();
  };

  const handleAddMilestone = async (outcomeId: string) => {
    const title = prompt('Milestone title:');
    if (!title) return;
    await addOutcomeMilestone(outcomeId, userId, title);
    await load();
  };

  const handleToggleMilestone = async (milestoneId: string, achieved: boolean) => {
    await toggleOutcomeMilestone(milestoneId, achieved);
    await load();
  };

  const handleDeleteMilestone = async (id: string) => {
    await deleteOutcomeMilestone(id);
    await load();
  };

  const handleSaveFeedback = async (outcomeId: string) => {
    const text = coachFeedback[outcomeId];
    if (!text) return;
    await updateOutcome(outcomeId, { coach_feedback: text });
    await load();
  };

  const handleDeleteOutcome = async (id: string) => {
    if (!confirm('Delete this outcome?')) return;
    await deleteOutcome(id);
    await load();
  };

  const handleUpgradeGoal = (goal: Goal) => {
    setPrefillGoal(goal);
    setShowBuilder(true);
  };

  if (isLoading) return <p className="text-sm text-chocolate-400">Loading outcomes...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-sage-600" />
          <h3 className="freud-heading-lg text-chocolate-900">Collaborative Outcomes</h3>
          {outcomes.length > 0 && (
            <span className="text-xs text-chocolate-400">({outcomes.filter(o => o.status === 'proposed').length} proposed)</span>
          )}
        </div>
        <button
          onClick={() => { setPrefillGoal(null); setShowBuilder(true); }}
          className="freud-btn-primary flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          New Outcome
        </button>
      </div>

      {/* Shared goals that can be upgraded */}
      {goals.length > 0 && (
        <div className="bg-cream-50 border border-cream-200 rounded-xl p-4">
          <p className="text-xs font-medium text-chocolate-600 mb-2">Client goals shared with you</p>
          <div className="space-y-1.5">
            {goals.map(g => (
              <div key={g.id} className="flex items-center gap-2">
                <span className="text-sm text-chocolate-700">{g.title}</span>
                {!outcomes.some(o => o.upgraded_from_goal_id === g.id) && (
                  <button
                    onClick={() => handleUpgradeGoal(g)}
                    className="text-xs font-medium text-sage-600 hover:text-sage-700 underline"
                  >
                    Develop into Outcome
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {outcomes.length === 0 ? (
        <div className="freud-card-elevated p-8 text-center">
          <Target className="w-10 h-10 text-cream-400 mx-auto mb-3" />
          <p className="text-sm text-chocolate-500 mb-1">No collaborative outcomes yet.</p>
          <p className="text-xs text-chocolate-400">Create an NLP Well-Formed Outcome for this client.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {outcomes.map(outcome => {
            const isExpanded = expandedOutcome === outcome.id;
            const milestones = outcome.milestones || [];
            const achievedCount = milestones.filter(m => m.achieved).length;
            const pct = milestones.length > 0 ? Math.round((achievedCount / milestones.length) * 100) : 0;
            const config = STATUS_CONFIG[outcome.status] || STATUS_CONFIG.draft;

            return (
              <div key={outcome.id} className="freud-card-elevated p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${config.color}`}>{config.label}</span>
                      <span className="text-[11px] text-chocolate-400 capitalize">{outcome.goal_type}</span>
                      {outcome.timeframe && (
                        <span className="text-[11px] text-chocolate-400">by {new Date(outcome.timeframe).toLocaleDateString()}</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-chocolate-900">
                      {outcome.outcome_statement || outcome.desired_outcome || 'Untitled outcome'}
                    </p>
                    {outcome.purpose && (
                      <p className="text-xs text-chocolate-500 mt-0.5">Why: {outcome.purpose}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setExpandedOutcome(isExpanded ? null : outcome.id)}
                    className="p-1.5 rounded-lg text-chocolate-400 hover:bg-cream-100"
                  >
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>

                {/* Progress bar */}
                {milestones.length > 0 && (
                  <div className="mt-3">
                    <div className="flex justify-between mb-1">
                      <span className="text-[11px] text-chocolate-500">{achievedCount}/{milestones.length} milestones</span>
                      <span className="text-[11px] font-bold text-chocolate-700">{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-cream-200 rounded-full overflow-hidden">
                      <div className="h-full bg-sage-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}

                {isExpanded && (
                  <div className="mt-4 pt-4 border-t border-cream-200 space-y-3">
                    {/* Full NLP fields */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        ['Desired outcome', outcome.desired_outcome],
                        ['Positive direction', outcome.positive_direction],
                        ['Purpose', outcome.purpose],
                        ['Evidence', outcome.evidence],
                        ['Baseline', outcome.measurement_baseline],
                        ['Target', outcome.measurement_target],
                        ['Context', outcome.context],
                        ['Personal control', outcome.personal_control],
                        ['Resources', outcome.resources],
                        ['Ecology', outcome.ecology],
                        ['Obstacles', outcome.obstacles],
                        ['Milestones', outcome.milestones_text],
                        ['First action', outcome.first_action],
                      ].map(([label, val]) => val ? (
                        <div key={label as string}>
                          <p className="text-[11px] font-medium text-chocolate-400">{label as string}</p>
                          <p className="text-xs text-chocolate-700">{val as string}</p>
                        </div>
                      ) : null)}
                    </div>

                    {outcome.commitment_level && (
                      <div className="flex gap-4 text-xs">
                        <span className="text-chocolate-500">Commitment: <strong className="text-chocolate-800">{outcome.commitment_level}/10</strong></span>
                        <span className="text-chocolate-500">Confidence: <strong className="text-chocolate-800">{outcome.confidence_level}/10</strong></span>
                      </div>
                    )}

                    {outcome.client_feedback && (
                      <div className="bg-cream-50 rounded-lg p-3">
                        <p className="text-[11px] font-medium text-chocolate-400 mb-1">Client feedback</p>
                        <p className="text-xs text-chocolate-700 italic">"{outcome.client_feedback}"</p>
                      </div>
                    )}

                    {/* Milestones management */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium text-chocolate-600">Milestones</p>
                        <button onClick={() => handleAddMilestone(outcome.id)} className="text-xs text-sage-600 hover:text-sage-700 font-medium">
                          + Add
                        </button>
                      </div>
                      {milestones.map(m => (
                        <div key={m.id} className="flex items-center gap-2 py-1">
                          <button
                            onClick={() => handleToggleMilestone(m.id, !m.achieved)}
                            className={`w-4 h-4 rounded-full border flex items-center justify-center flex-shrink-0 ${
                              m.achieved ? 'bg-sage-500 border-sage-500' : 'border-cream-300 hover:border-sage-400'
                            }`}
                          >
                            {m.achieved && <CheckCircle className="w-3 h-3 text-white" />}
                          </button>
                          <span className={`text-xs flex-1 ${m.achieved ? 'text-chocolate-400 line-through' : 'text-chocolate-700'}`}>
                            {m.title}
                          </span>
                          {m.target_date && <span className="text-[10px] text-chocolate-400">{new Date(m.target_date).toLocaleDateString()}</span>}
                          <button onClick={() => handleDeleteMilestone(m.id)} className="text-chocolate-300 hover:text-red-500">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Coach feedback */}
                    <div>
                      <p className="text-xs font-medium text-chocolate-600 mb-1">Coach feedback (visible to client)</p>
                      <div className="flex gap-2">
                        <input
                          className="freud-input text-xs flex-1"
                          placeholder="Add feedback for your client..."
                          value={coachFeedback[outcome.id] ?? outcome.coach_feedback ?? ''}
                          onChange={(e) => setCoachFeedback(prev => ({ ...prev, [outcome.id]: e.target.value }))}
                        />
                        <button onClick={() => handleSaveFeedback(outcome.id)} className="freud-btn-secondary text-xs px-3">
                          Save
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <button onClick={() => handleDeleteOutcome(outcome.id)} className="text-xs text-red-500 hover:text-red-600 font-medium">
                        Delete outcome
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <OutcomeBuilderModal
        isOpen={showBuilder}
        onClose={() => { setShowBuilder(false); setPrefillGoal(null); }}
        onSubmit={handleCreate}
        targetUserId={userId}
        coachId={coachId}
        prefillGoal={prefillGoal ? { id: prefillGoal.id, title: prefillGoal.title, description: prefillGoal.description, target_date: prefillGoal.target_date } : null}
      />
    </div>
  );
}
